import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import ThemeToggle from "@/components/ThemeToggle";
import OfflineBanner from "@/components/OfflineBanner";
import SyncStatus from "@/components/SyncStatus";
import {
  Layers,
  Thermometer,
  RotateCcw,
  Package,
  Activity,
  Leaf,
  LogOut,
  Monitor,
} from "lucide-react";

const TABS = [
  { to: "/app/operator", icon: Layers, label: "My Rows", end: true },
  { to: "/app/operator/log-temp", icon: Thermometer, label: "Temp", end: false },
  { to: "/app/operator/log-turn", icon: RotateCcw, label: "Turn", end: false },
  { to: "/app/operator/log-material", icon: Package, label: "Material", end: false },
  { to: "/app/operator/status", icon: Activity, label: "Status", end: false },
];

export default function OperatorLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const isMappingPage = location.pathname.endsWith("/location");

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-card focus:p-3 focus:rounded-lg">Skip to content</a>
      {/* Offline status banner */}
      <OfflineBanner />

      {/* Sticky top header */}
      <header className="h-14 bg-card/80 backdrop-blur-md border-b border-border flex items-center justify-between px-4 sticky top-0 z-30 select-none">
        <Link to="/app/operator" className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-forest-600 flex items-center justify-center">
            <Leaf size={14} className="text-white" />
          </div>
          <span className="text-sm font-bold text-foreground tracking-tight">
            Compost<span className="text-forest-600 dark:text-forest-400">Dev</span>
          </span>
        </Link>
        <div className="flex items-center gap-2">
          <SyncStatus />
          <Link
            to="/app"
            aria-label="Open manager app"
            title="Manager"
            className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg border border-border hover:bg-secondary transition-colors text-xs font-semibold text-muted-foreground"
          >
            <Monitor size={14} className="text-forest-600 dark:text-forest-400" />
            <span className="hidden sm:inline">Manager</span>
          </Link>
          <ThemeToggle />
          <button
            onClick={logout}
            className="p-2 text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary transition-colors"
            title="Sign out" aria-label="Sign out"
          >
            <LogOut size={16} />
          </button>
        </div>
      </header>

      {/* Content area — full width, scrollable */}
      <main id="main-content" tabIndex={-1} className={`flex-1 px-4 py-4 overflow-auto ${isMappingPage ? "pb-6" : "pb-28"}`}>
        <Outlet />
      </main>

      {/* Bottom tab navigation */}
      {!isMappingPage && (
        <nav aria-label="Operator navigation" className="fixed bottom-0 left-0 right-0 bg-card/95 backdrop-blur-md border-t border-border z-30 safe-area-bottom select-none">
          <div className="flex items-center justify-around h-16 max-w-lg mx-auto">
            {TABS.map(({ to, icon: Icon, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-1 min-w-0 flex-1 min-h-[48px] px-2 py-1.5 rounded-xl transition-all ${
                    isActive
                      ? "text-forest-600 dark:text-forest-400"
                      : "text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <div className={`p-1.5 rounded-lg transition-colors ${
                      isActive ? "bg-forest-100 dark:bg-forest-900/30" : ""
                    }`}>
                      <Icon size={20} strokeWidth={isActive ? 2.25 : 1.75} />
                    </div>
                    <span className={`text-xs font-semibold ${
                      isActive ? "text-forest-700 dark:text-forest-300" : ""
                    }`}>
                      {label}
                    </span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}
